import { useParams, useNavigate } from 'react-router-dom';
import { useEffect, useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import AppShell from '@/components/AppShell';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { UserCheck, AlertTriangle, CheckCircle, X } from 'lucide-react';
import { trackDeepLinkUsage } from '@/utils/deepLinkUtils';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';

const ProfileHandover = () => {
  const { token } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { t } = useTranslation('profile');
  const [tokenData, setTokenData] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Track deep link usage
  useEffect(() => {
    if (token) {
      trackDeepLinkUsage('profile_handover', 'view', { token });
    }
  }, [token]);

  // Validate token on load
  useEffect(() => {
    const validateToken = async () => {
      if (!token || !user) {
        setError('Invalid token or user not authenticated');
        setLoading(false);
        return;
      }

      try {
        const { data, error } = await supabase
          .from('profile_handover_tokens')
          .select(`
            *,
            profiles!profile_handover_tokens_profile_id_fkey(
              username, first_name, last_name
            )
          `)
          .eq('token_code', token)
          .eq('is_used', false)
          .gt('expires_at', new Date().toISOString())
          .single();

        if (error || !data) {
          setError('Invalid or expired handover token');
        } else {
          setTokenData(data);
        }
      } catch (err) {
        setError('Failed to validate token');
      } finally {
        setLoading(false);
      }
    };

    validateToken();
  }, [token, user]);

  // Claim profile mutation
  const claimProfileMutation = useMutation({
    mutationFn: async () => {
      if (!token || !user?.id) throw new Error('Missing required data');
      
      const { data, error } = await supabase.rpc('claim_profile_with_token', {
        token_code_param: token,
        new_owner_id: user.id
      });

      if (error) throw error;
      return data;
    },
    onSuccess: (data: any) => {
      trackDeepLinkUsage('profile_handover', 'claim_success', { 
        token, 
        profileId: data.profile_id 
      });
      
      toast.success('Profile successfully claimed!');
      navigate(`/profile/${data.username}`);
    },
    onError: (error: any) => {
      toast.error(error.message || 'Failed to claim profile');
      trackDeepLinkUsage('profile_handover', 'claim_error', { 
        token, 
        error: error.message 
      });
    }
  });

  const handleClaimProfile = () => {
    claimProfileMutation.mutate();
  };

  const handleDecline = () => {
    navigate('/dashboard');
  };

  if (!user) {
    return (
      <AppShell 
        showHeader={false} 
        showNavigation={false}
        backgroundGradient="linear-gradient(154deg, #9edfff 2%, rgb(191, 234, 255) 26%, rgb(212, 204, 255) 100%)"
      >
        <div className="container mx-auto py-8 px-4 flex flex-col items-center justify-center min-h-[80vh]">
          <div className="text-center max-w-md">
            <div className="mb-6 w-16 h-16 rounded-full bg-yellow-100 flex items-center justify-center mx-auto">
              <AlertTriangle className="h-8 w-8 text-yellow-600" />
            </div>
            <h1 className="text-2xl font-bold text-gray-900 mb-3">Authentication Required</h1>
            <p className="text-gray-600 mb-6">
              Please log in to claim this profile
            </p>
            <Button onClick={() => navigate('/login')}>
              Log In
            </Button>
          </div>
        </div>
      </AppShell>
    );
  }

  if (loading) {
    return (
      <AppShell 
        showHeader={false} 
        showNavigation={false}
        backgroundGradient="linear-gradient(154deg, #9edfff 2%, rgb(191, 234, 255) 26%, rgb(212, 204, 255) 100%)"
      >
        <div className="container mx-auto py-8 px-4 flex flex-col items-center justify-center min-h-[80vh]">
          <div className="text-center">Validating handover token...</div>
        </div>
      </AppShell>
    );
  }

  if (error || !tokenData) {
    return (
      <AppShell 
        showHeader={false} 
        showNavigation={false}
        backgroundGradient="linear-gradient(154deg, #9edfff 2%, rgb(191, 234, 255) 26%, rgb(212, 204, 255) 100%)"
      >
        <div className="container mx-auto py-8 px-4 flex flex-col items-center justify-center min-h-[80vh]">
          <div className="text-center max-w-md">
            <div className="mb-6 w-16 h-16 rounded-full bg-red-100 flex items-center justify-center mx-auto">
              <X className="h-8 w-8 text-red-500" />
            </div>
            <h1 className="text-2xl font-bold text-gray-900 mb-3">Invalid Token</h1>
            <p className="text-gray-600 mb-6">
              {error || 'This handover token is invalid or has expired'}
            </p>
            <Button onClick={() => navigate('/dashboard')}>
              Back to Dashboard
            </Button>
          </div>
        </div>
      </AppShell>
    );
  }

  const profile = tokenData.profiles;
  const expiresAt = new Date(tokenData.expires_at);
  const timeLeft = Math.ceil((expiresAt.getTime() - Date.now()) / (1000 * 60 * 60)); // hours

  return (
    <AppShell 
      showHeader={true} 
      showNavigation={false}
      backgroundGradient="linear-gradient(154deg, #9edfff 2%, rgb(191, 234, 255) 26%, rgb(212, 204, 255) 100%)"
    >
      <div className="container mx-auto py-8 px-4 max-w-2xl">
        <div className="text-center mb-8">
          <div className="mb-4 w-16 h-16 rounded-full bg-uplivion-blue/10 flex items-center justify-center mx-auto">
            <UserCheck className="h-8 w-8 text-uplivion-blue" />
          </div>
          <h1 className="text-3xl font-bold text-uplivion-blue mb-2">
            Profile Handover
          </h1>
          <p className="text-gray-600">
            You've been invited to claim a profile
          </p>
        </div>

        <Card className="uplivion-card mb-6">
          <CardHeader>
            <CardTitle>Profile Details</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex items-center gap-4 mb-4">
              <div className="w-16 h-16 rounded-full bg-uplivion-blue/10 flex items-center justify-center">
                <UserCheck className="h-8 w-8 text-uplivion-blue" />
              </div>
              <div>
                <h3 className="text-xl font-semibold">
                  {profile.first_name} {profile.last_name}
                </h3>
                <p className="text-muted-foreground">@{profile.username}</p>
              </div>
            </div>
            
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <div className="flex items-start gap-3">
                <CheckCircle className="h-5 w-5 text-blue-600 mt-0.5" />
                <div>
                  <p className="text-sm font-medium text-blue-900">
                    Profile Transfer Available
                  </p>
                  <p className="text-sm text-blue-700">
                    This profile is ready to be transferred to your account. 
                    You will become the owner and can manage it fully.
                  </p>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="uplivion-card mb-6">
          <CardHeader>
            <CardTitle>What happens when you claim this profile?</CardTitle>
          </CardHeader>
          <CardContent>
            <ul className="space-y-2 text-sm">
              <li className="flex items-start gap-2">
                <CheckCircle className="h-4 w-4 text-green-600 mt-0.5" />
                <span>The profile will be transferred to your account</span>
              </li>
              <li className="flex items-start gap-2">
                <CheckCircle className="h-4 w-4 text-green-600 mt-0.5" />
                <span>You'll have full access to manage the timeline and memories</span>
              </li>
              <li className="flex items-start gap-2">
                <CheckCircle className="h-4 w-4 text-green-600 mt-0.5" />
                <span>All existing memories and relationships will be preserved</span>
              </li>
              <li className="flex items-start gap-2">
                <CheckCircle className="h-4 w-4 text-green-600 mt-0.5" />
                <span>The handover token will be permanently used</span>
              </li>
            </ul>
          </CardContent>
        </Card>

        {timeLeft > 0 && (
          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-6">
            <div className="flex items-center gap-2">
              <AlertTriangle className="h-5 w-5 text-yellow-600" />
              <p className="text-sm font-medium text-yellow-900">
                Token expires in {timeLeft} hour{timeLeft !== 1 ? 's' : ''}
              </p>
            </div>
          </div>
        )}

        <div className="flex gap-4">
          <Button
            onClick={handleClaimProfile}
            disabled={claimProfileMutation.isPending}
            className="flex-1"
          >
            {claimProfileMutation.isPending ? 'Claiming...' : 'Claim Profile'}
          </Button>
          
          <Button
            variant="outline"
            onClick={handleDecline}
            disabled={claimProfileMutation.isPending}
            className="flex-1"
          >
            Decline
          </Button>
        </div>
      </div>
    </AppShell>
  );
};

export default ProfileHandover;